import { useMutation, UseMutationOptions, useQuery, useQueryClient } from 'react-query';

import { getRequest, postRequest, putRequest } from 'services/apiRequests';
import { CSRFService } from 'services/csrf';
import { spmActivityKeys } from 'services/threads';
import type { APIList } from 'types';
import type { ApprovalGroupDetail, SPMApproval, SPMWorkflowRunDetail, SPMWorkflowSummary } from 'types/SPM';
import type { APIError } from 'utils/errors';
import { QueryHookOptions } from 'types/ReactQuery';

export type { SPMApproval, SPMApprovalGroup, SPMWorkflowRunDetail, SPMWorkflowSummary } from 'types/SPM';

const apiUrl = '/api/v1/spm/approvals/';
const workflowsApiUrl = '/api/v1/spm/workflows/';

interface SPMApprovalsListResponse {
  data: SPMApproval[];
  total_count: number;
}

interface SPMWorkflowsListResponse {
  data: SPMWorkflowSummary[];
  total_count: number;
}

const listSPMApprovals = () => getRequest<null, SPMApprovalsListResponse>(apiUrl);

const spmApprovalsKeys = {
  base: () => ['spm-approvals'] as const,
  list: () => [...spmApprovalsKeys.base(), 'list'] as const,
  detail: (id: string) => [...spmApprovalsKeys.base(), 'detail', id] as const,
  approvalGroups: (objectId: string) => [...spmApprovalsKeys.base(), 'approval-groups', objectId] as const,
  workflows: () => [...spmApprovalsKeys.base(), 'workflows'] as const,
  workflow: (workflowId: string) => [...spmApprovalsKeys.base(), 'workflow', workflowId] as const,
};

const useListSPMApprovalsQuery = (
  options: QueryHookOptions<SPMApprovalsListResponse, APIError, SPMApproval[]> = {},
) => {
  const QUERY_KEY = spmApprovalsKeys.list();
  const queryClient = useQueryClient();

  const query = useQuery<SPMApprovalsListResponse, APIError, SPMApproval[]>({
    select: (response) => response.data || [],
    ...options,
    queryKey: QUERY_KEY,
    queryFn: () => listSPMApprovals(),
  });

  const invalidateQuery = () => queryClient.invalidateQueries({ queryKey: QUERY_KEY });

  return { query, invalidateQuery };
};

// Get single approval by ID
const getApproval = (approvalId: string) => getRequest<null, SPMApproval>(`${apiUrl}${approvalId}/`);

const useGetApprovalQuery = (approvalId: string | undefined, options: QueryHookOptions<SPMApproval, APIError> = {}) => {
  const QUERY_KEY = spmApprovalsKeys.detail(approvalId ?? '');

  return useQuery<SPMApproval, APIError>({
    ...options,
    queryKey: QUERY_KEY,
    queryFn: () => getApproval(approvalId as string),
  });
};

// Approve/reject uses the general approval-tasks endpoint
const approvalTasksApiUrl = '/api/v1/approval-tasks/';

type ApprovalUpdateRequest = {
  is_approved: boolean;
  message?: string | null;
};

const updateApprovalTask = (csrfToken: string, approvalId: string) => (data: ApprovalUpdateRequest) =>
  putRequest<ApprovalUpdateRequest, SPMApproval>(`${approvalTasksApiUrl}${approvalId}/`, data, csrfToken);

const useUpdateApprovalMutation = (
  approvalId: string,
  options: UseMutationOptions<SPMApproval, APIError, ApprovalUpdateRequest> = {},
) => {
  const queryClient = useQueryClient();
  const { query: csrfQuery } = CSRFService.useGetCSRFToken();
  const csrfToken = csrfQuery.data?.csrf ?? '';

  return useMutation<SPMApproval, APIError, ApprovalUpdateRequest>({
    ...options,
    onSuccess: (data, variables, context, mutationContext) => {
      queryClient.invalidateQueries({ queryKey: spmApprovalsKeys.base() });
      // Submitting writes the change logs the activity feed reads, so its corner indicators are stale.
      queryClient.invalidateQueries({ queryKey: spmActivityKeys.base() });
      options.onSuccess?.(data, variables, context, mutationContext);
    },
    mutationFn: updateApprovalTask(csrfToken, approvalId),
  });
};

// Override approvals via the approval-overrides bulk-create endpoint
const approvalOverridesApiUrl = '/api/v1/approval-overrides/';

type ApprovalOverrideBulkCreateRequest = {
  approval_ids: string[];
  is_approved: boolean;
  message: string;
};

const bulkCreateApprovalOverrides = (csrfToken: string) => (data: ApprovalOverrideBulkCreateRequest) =>
  postRequest<ApprovalOverrideBulkCreateRequest, unknown>(`${approvalOverridesApiUrl}bulk-create/`, data, csrfToken);

const useCreateApprovalOverrideMutation = (
  options: UseMutationOptions<unknown, APIError, ApprovalOverrideBulkCreateRequest> = {},
) => {
  const queryClient = useQueryClient();
  const { query: csrfQuery } = CSRFService.useGetCSRFToken();
  const csrfToken = csrfQuery.data?.csrf ?? '';

  return useMutation<unknown, APIError, ApprovalOverrideBulkCreateRequest>({
    ...options,
    onSuccess: (data, variables, context, mutationContext) => {
      queryClient.invalidateQueries({ queryKey: spmApprovalsKeys.base() });
      queryClient.invalidateQueries({ queryKey: spmActivityKeys.base() });
      options.onSuccess?.(data, variables, context, mutationContext);
    },
    mutationFn: bulkCreateApprovalOverrides(csrfToken),
  });
};

// Approval activity feed — fetches approval groups for a given object
const approvalGroupsFeedUrl = '/api/v1/approval-groups/feed/';

const getApprovalGroupsForObject = (objectId: string) =>
  getRequest<null, APIList<ApprovalGroupDetail>>(
    `${approvalGroupsFeedUrl}?object_id=${objectId}&object_type=spm_proposed_change_group`,
  );

const useApprovalGroupsQuery = (
  objectId: string | undefined,
  options: QueryHookOptions<APIList<ApprovalGroupDetail>, APIError> = {},
) => {
  return useQuery<APIList<ApprovalGroupDetail>, APIError>({
    ...options,
    queryKey: spmApprovalsKeys.approvalGroups(objectId ?? ''),
    queryFn: () => getApprovalGroupsForObject(objectId as string),
  });
};

// Apply proposed changes via the workflow endpoint
type ApplyChangesResponse = {
  status: string;
  total_applied: number;
  affected_objects: number;
};

const applyProposedChanges = (csrfToken: string, workflowId: string) => () =>
  postRequest<Record<string, never>, ApplyChangesResponse>(
    `${workflowsApiUrl}${workflowId}/apply-changes/`,
    {},
    csrfToken,
  );

const useApplyProposedChangesMutation = (
  workflowId: string | undefined,
  options: UseMutationOptions<ApplyChangesResponse, APIError, void> = {},
) => {
  const queryClient = useQueryClient();
  const { query: csrfQuery } = CSRFService.useGetCSRFToken();
  const csrfToken = csrfQuery.data?.csrf ?? '';

  return useMutation<ApplyChangesResponse, APIError, void>({
    ...options,
    onSuccess: (data, variables, context, mutationContext) => {
      queryClient.invalidateQueries({ queryKey: spmApprovalsKeys.base() });
      options.onSuccess?.(data, variables, context, mutationContext);
    },
    mutationFn: applyProposedChanges(csrfToken, workflowId ?? ''),
  });
};

// Admin workflows list
const listSPMWorkflows = () => getRequest<null, SPMWorkflowsListResponse>(workflowsApiUrl);

const useListSPMWorkflowsQuery = (
  options: QueryHookOptions<SPMWorkflowsListResponse, APIError, SPMWorkflowSummary[]> = {},
) => {
  const QUERY_KEY = spmApprovalsKeys.workflows();

  const query = useQuery<SPMWorkflowsListResponse, APIError, SPMWorkflowSummary[]>({
    select: (response) => response.data || [],
    ...options,
    queryKey: QUERY_KEY,
    queryFn: () => listSPMWorkflows(),
  });

  return { query };
};

const getSPMWorkflow = (workflowId: string) =>
  getRequest<null, SPMWorkflowRunDetail>(`${workflowsApiUrl}${workflowId}/`);

const useGetSPMWorkflowQuery = (
  workflowId: string | undefined,
  options: QueryHookOptions<SPMWorkflowRunDetail, APIError> = {},
) =>
  useQuery<SPMWorkflowRunDetail, APIError>({
    enabled: !!workflowId,
    ...options,
    queryKey: spmApprovalsKeys.workflow(workflowId ?? ''),
    queryFn: () => getSPMWorkflow(workflowId as string),
  });

export const SPMApprovalsService = {
  apiUrl,
  workflowsApiUrl,
  listSPMApprovals,
  getApproval,
  spmApprovalsKeys,
  useListSPMApprovalsQuery,
  useGetApprovalQuery,
  useUpdateApprovalMutation,
  useCreateApprovalOverrideMutation,
  useApprovalGroupsQuery,
  useApplyProposedChangesMutation,
  useListSPMWorkflowsQuery,
  getSPMWorkflow,
  useGetSPMWorkflowQuery,
};
